import { NetworkStatus } from '@apollo/client';

import { normalizeChatUuid } from './notification-helpers';
import type { GetChatWithMessages } from '../../../queries/chats';

interface ChatReadState {
  activeStreamId?: string | null;
  hasUnreadMessages?: boolean | null;
}

export function getCurrentChatWithMessages({
  chatUuid,
  isNewChat,
  chat,
}: {
  chatUuid?: string;
  isNewChat: boolean;
  chat?: GetChatWithMessages | null;
}): GetChatWithMessages | undefined {
  if (!chatUuid || isNewChat || !chat) {
    return undefined;
  }

  return chat.uuid === normalizeChatUuid(chatUuid) ? chat : undefined;
}

export function getIsMessagesLoading({
  chat,
  currentChat,
  isNewChat,
  networkStatus,
}: {
  chat?: GetChatWithMessages | null;
  currentChat?: GetChatWithMessages;
  isNewChat: boolean;
  networkStatus: NetworkStatus;
}): boolean {
  if (isNewChat || currentChat) {
    return false;
  }

  if (chat && !currentChat) {
    return true;
  }

  return (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.refetch
  );
}

/** The server answered for this chat and has no such chat, e.g. it lives in another region. */
export function getIsChatMissing({
  chat,
  hasError,
  isNewChat,
  networkStatus,
}: {
  chat?: GetChatWithMessages | null;
  hasError: boolean;
  isNewChat: boolean;
  networkStatus: NetworkStatus;
}): boolean {
  return !isNewChat && !hasError && networkStatus === NetworkStatus.ready && chat === null;
}

export function shouldMarkChatAsReadOnOpen(chat?: ChatReadState | null): boolean {
  return Boolean(chat?.hasUnreadMessages && !chat.activeStreamId);
}

export function getVisibleChatActivationKey({
  chatUuid,
  isChatVisible,
  isNewChat,
}: {
  chatUuid?: string;
  isChatVisible: boolean;
  isNewChat: boolean;
}): string | null {
  if (!isChatVisible || !chatUuid) {
    return null;
  }

  const chatState = isNewChat ? 'new' : 'existing';

  return `${chatState}:${normalizeChatUuid(chatUuid)}`;
}
